import type {
  AssignmentDeliveryRuleReviewer,
  AssignmentDeliveryRuleTarget,
  AssignmentSubmission,
  AssignmentSubmissionType
} from './model';
import { parseIsoDateTime } from '../shared/isoDate';

const AUDIO_MAX_SIZE_BYTES = 200 * 1024 * 1024;
const VIDEO_MAX_SIZE_BYTES = 1024 * 1024 * 1024;

const AUDIO_EXTENSIONS = new Set(['aac', 'flac', 'm4a', 'mp3', 'ogg', 'opus', 'wav', 'webm']);
const VIDEO_EXTENSIONS = new Set(['m4v', 'mkv', 'mov', 'mp4', 'webm']);
const AUDIO_MIME_TYPES = new Set([
  'audio/aac',
  'audio/flac',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg',
  'audio/opus',
  'audio/wav',
  'audio/webm',
  'audio/x-m4a',
  'audio/x-wav'
]);
const VIDEO_MIME_TYPES = new Set(['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v', 'video/x-matroska']);

type SelectorInput<T> = T | readonly T[];

interface AssignmentRuleMember {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroupIds: readonly string[];
  readonly memberGroupTagIds: readonly string[];
}

interface SelectorReferenceSets {
  readonly memberIds: ReadonlySet<string>;
  readonly memberGroupIds: ReadonlySet<string>;
  readonly memberGroupTagIds: ReadonlySet<string>;
}

export interface CompletedAssignmentUpload {
  readonly fileUploadId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly assignmentId: string;
  readonly createdBySeasonMembershipId: string;
  readonly objectKey: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly status: 'completed';
}

export interface AssignmentSubmissionCleanupWarning {
  readonly objectKey: string;
  readonly message: string;
}

export function normalizeAssignmentDraft(input: {
  readonly pieceName: string;
  readonly dueAt: string;
  readonly submissionType: AssignmentSubmissionType;
  readonly description?: string;
}): {
  readonly pieceName: string;
  readonly dueAt: string;
  readonly submissionType: AssignmentSubmissionType;
  readonly description: string;
} {
  const pieceName = input.pieceName.trim();
  if (pieceName === '') {
    throw new Error('Assignment piece name is required');
  }

  assertValidIsoDateString(input.dueAt, 'Assignment due time must be valid');

  if (input.submissionType !== 'audio' && input.submissionType !== 'video') {
    throw new Error('Assignment submission type must be audio or video');
  }

  return {
    pieceName,
    dueAt: input.dueAt,
    submissionType: input.submissionType,
    description: input.description?.trim() ?? ''
  };
}

export function resolveDeliveryRules(input: {
  readonly deliveryRules: readonly {
    readonly target: SelectorInput<AssignmentDeliveryRuleTarget>;
    readonly reviewers: SelectorInput<AssignmentDeliveryRuleReviewer>;
  }[];
  readonly members: readonly AssignmentRuleMember[];
  readonly knownMemberGroupIds: readonly string[];
  readonly knownMemberGroupTagIds: readonly string[];
}): readonly {
  readonly submitterIds: readonly string[];
  readonly reviewerIds: readonly string[];
}[] {
  const references = buildSelectorReferenceSets({
    members: input.members,
    knownMemberGroupIds: input.knownMemberGroupIds,
    knownMemberGroupTagIds: input.knownMemberGroupTagIds
  });
  const seenSubmitters = new Set<string>();
  const duplicateSubmitters = new Set<string>();

  const resolved = input.deliveryRules.map((deliveryRule) => {
    const targets = asArray(deliveryRule.target);
    const reviewers = asArray(deliveryRule.reviewers);

    validateTargetSelectors(targets, references);
    validateReviewerSelectors(reviewers, references);

    const submitterIds = resolveTargetSubmitters(targets, input.members);
    const reviewerIds = resolveReviewers(reviewers, input.members);

    if (submitterIds.length > 0 && reviewerIds.length === 0) {
      throw new Error('Assignment delivery rule needs a usable reviewer');
    }

    for (const submitterId of submitterIds) {
      if (!reviewerIds.some((reviewerId) => reviewerId !== submitterId)) {
        throw new Error('Assignment reviewer cannot review their own submission');
      }
    }

    for (const submitterId of submitterIds) {
      if (seenSubmitters.has(submitterId)) {
        duplicateSubmitters.add(submitterId);
      } else {
        seenSubmitters.add(submitterId);
      }
    }

    return { submitterIds, reviewerIds };
  });

  if (duplicateSubmitters.size > 0) {
    const duplicateNames = input.members
      .filter((member) => duplicateSubmitters.has(member.seasonMembershipId))
      .map((member) => member.nickname);
    throw new Error(`Duplicate assignment submitters: ${duplicateNames.join('、')}`);
  }

  return resolved;
}

export function validateAssignmentSubmissionFile(input: {
  readonly submissionType: AssignmentSubmissionType;
  readonly fileName: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
}): void {
  const fileName = input.fileName.trim();
  const mimeType = input.mimeType.trim().toLowerCase();
  const extension = getFileExtension(fileName);

  if (fileName === '' || mimeType === '' || !Number.isSafeInteger(input.sizeBytes) || input.sizeBytes <= 0) {
    throw new Error('Assignment file is required');
  }

  if (input.submissionType === 'audio' && input.sizeBytes > AUDIO_MAX_SIZE_BYTES) {
    throw new Error('Assignment audio file is too large');
  }

  if (input.submissionType === 'video' && input.sizeBytes > VIDEO_MAX_SIZE_BYTES) {
    throw new Error('Assignment video file is too large');
  }

  if (input.submissionType === 'audio') {
    if (!AUDIO_EXTENSIONS.has(extension) || !AUDIO_MIME_TYPES.has(mimeType)) {
      throw new Error('Assignment file type does not match submission type');
    }
    return;
  }

  if (input.submissionType === 'video') {
    if (!VIDEO_EXTENSIONS.has(extension) || !VIDEO_MIME_TYPES.has(mimeType)) {
      throw new Error('Assignment file type does not match submission type');
    }
    return;
  }

  throw new Error('Assignment submission type must be audio or video');
}

export function assertCanSubmitAssignment(input: { readonly nowIso: string; readonly dueAt: string }): void {
  const nowTime = assertValidIsoDateString(input.nowIso, 'Assignment due time must be valid');
  const dueTime = assertValidIsoDateString(input.dueAt, 'Assignment due time must be valid');

  if (nowTime >= dueTime) {
    throw new Error('Assignment deadline has passed');
  }
}

export function applyAssignmentSubmission(input: {
  readonly existingSubmission: AssignmentSubmission | null;
  readonly completedUpload: CompletedAssignmentUpload;
  readonly note: string;
  readonly nowIso: string;
  readonly newSubmissionId: () => string;
}): {
  readonly submission: AssignmentSubmission;
  readonly oldObjectKeyToDelete: string | null;
} {
  assertValidIsoDateString(input.nowIso, 'Assignment due time must be valid');

  if (!input.completedUpload || input.completedUpload.status !== 'completed') {
    throw new Error('Completed assignment upload is required');
  }

  if (
    input.existingSubmission !== null &&
    (input.existingSubmission.assignmentId !== input.completedUpload.assignmentId ||
      input.existingSubmission.choirId !== input.completedUpload.choirId ||
      input.existingSubmission.seasonId !== input.completedUpload.seasonId ||
      input.existingSubmission.submitterSeasonMembershipId !== input.completedUpload.createdBySeasonMembershipId)
  ) {
    throw new Error('Completed assignment upload does not match existing submission');
  }

  const submissionId = input.existingSubmission?.assignmentSubmissionId ?? input.newSubmissionId();
  const submission: AssignmentSubmission = {
    assignmentSubmissionId: submissionId,
    assignmentId: input.completedUpload.assignmentId,
    choirId: input.completedUpload.choirId,
    seasonId: input.completedUpload.seasonId,
    submitterSeasonMembershipId: input.completedUpload.createdBySeasonMembershipId,
    fileUploadId: input.completedUpload.fileUploadId,
    objectKey: input.completedUpload.objectKey,
    fileName: input.completedUpload.fileName,
    mimeType: input.completedUpload.mimeType,
    sizeBytes: input.completedUpload.sizeBytes,
    note: input.note.trim(),
    submittedAt: input.nowIso
  };

  return {
    submission,
    oldObjectKeyToDelete: input.existingSubmission?.objectKey ?? null
  };
}

export function getAssignmentSubmissionCleanupWarning(input: {
  readonly oldObjectKeyToDelete: string | null;
  readonly cleanupFailureMessage: string;
}): AssignmentSubmissionCleanupWarning | null {
  if (input.oldObjectKeyToDelete === null) {
    return null;
  }

  return {
    objectKey: input.oldObjectKeyToDelete,
    message: input.cleanupFailureMessage
  };
}

function resolveTargetSubmitters(
  targets: readonly AssignmentDeliveryRuleTarget[],
  members: readonly Pick<AssignmentRuleMember, 'seasonMembershipId' | 'memberGroupIds'>[]
): readonly string[] {
  const selectedIds = new Set<string>();
  const excludedIds = new Set<string>();

  for (const target of targets) {
    if (target.selectorType === 'member_group' && target.memberGroupId !== null) {
      for (const member of members) {
        if (member.memberGroupIds.includes(target.memberGroupId)) {
          selectedIds.add(member.seasonMembershipId);
        }
      }
    }

    if (target.selectorType === 'season_membership_include' && target.seasonMembershipId !== null) {
      selectedIds.add(target.seasonMembershipId);
    }

    if (target.selectorType === 'season_membership_exclude' && target.seasonMembershipId !== null) {
      excludedIds.add(target.seasonMembershipId);
    }
  }

  return orderedMemberIds(selectedIds, members).filter((memberId) => !excludedIds.has(memberId));
}

function resolveReviewers(
  reviewers: readonly AssignmentDeliveryRuleReviewer[],
  members: readonly Pick<AssignmentRuleMember, 'seasonMembershipId' | 'memberGroupIds' | 'memberGroupTagIds'>[]
): readonly string[] {
  const selectedIds = new Set<string>();

  for (const reviewer of reviewers) {
    if (reviewer.selectorType === 'member_group' && reviewer.memberGroupId !== null) {
      for (const member of members) {
        if (member.memberGroupIds.includes(reviewer.memberGroupId)) {
          selectedIds.add(member.seasonMembershipId);
        }
      }
    }

    if (reviewer.selectorType === 'member_group_tag' && reviewer.memberGroupTagId !== null) {
      for (const member of members) {
        if (member.memberGroupTagIds.includes(reviewer.memberGroupTagId)) {
          selectedIds.add(member.seasonMembershipId);
        }
      }
    }

    if (reviewer.selectorType === 'season_membership' && reviewer.seasonMembershipId !== null) {
      selectedIds.add(reviewer.seasonMembershipId);
    }
  }

  return orderedMemberIds(selectedIds, members);
}

function validateTargetSelectors(
  targets: readonly AssignmentDeliveryRuleTarget[],
  references: SelectorReferenceSets
): void {
  for (const target of targets) {
    if (target.selectorType === 'member_group') {
      assertOnlyMemberGroupSelectorFields(target);
      assertKnownMemberGroup(target.memberGroupId);
      continue;
    }

    if (target.selectorType === 'season_membership_include' || target.selectorType === 'season_membership_exclude') {
      assertOnlySeasonMembershipSelectorFields(target);
      assertKnownMember(target.seasonMembershipId);
      continue;
    }

    throw new Error('Assignment selector is invalid');
  }

  function assertKnownMember(memberId: string | null): asserts memberId is string {
    if (memberId === null || !references.memberIds.has(memberId)) {
      throw new Error('Assignment selector references an unknown member');
    }
  }

  function assertKnownMemberGroup(memberGroupId: string | null): asserts memberGroupId is string {
    if (memberGroupId === null || !references.memberGroupIds.has(memberGroupId)) {
      throw new Error('Assignment selector references an unknown member group');
    }
  }
}

function validateReviewerSelectors(
  reviewers: readonly AssignmentDeliveryRuleReviewer[],
  references: SelectorReferenceSets
): void {
  for (const reviewer of reviewers) {
    if (reviewer.selectorType === 'member_group') {
      assertOnlyReviewerMemberGroupFields(reviewer);
      assertKnownMemberGroup(reviewer.memberGroupId);
      continue;
    }

    if (reviewer.selectorType === 'member_group_tag') {
      assertOnlyReviewerMemberGroupTagFields(reviewer);
      assertKnownMemberGroupTag(reviewer.memberGroupTagId);
      continue;
    }

    if (reviewer.selectorType === 'season_membership') {
      assertOnlyReviewerSeasonMembershipFields(reviewer);
      assertKnownMember(reviewer.seasonMembershipId);
      continue;
    }

    throw new Error('Assignment selector is invalid');
  }

  function assertKnownMember(memberId: string | null): asserts memberId is string {
    if (memberId === null || !references.memberIds.has(memberId)) {
      throw new Error('Assignment selector references an unknown member');
    }
  }

  function assertKnownMemberGroup(memberGroupId: string | null): asserts memberGroupId is string {
    if (memberGroupId === null || !references.memberGroupIds.has(memberGroupId)) {
      throw new Error('Assignment selector references an unknown member group');
    }
  }

  function assertKnownMemberGroupTag(memberGroupTagId: string | null): asserts memberGroupTagId is string {
    if (memberGroupTagId === null || !references.memberGroupTagIds.has(memberGroupTagId)) {
      throw new Error('Assignment selector references an unknown member group tag');
    }
  }
}

function assertOnlyMemberGroupSelectorFields(selector: AssignmentDeliveryRuleTarget): void {
  if (selector.memberGroupId === null || selector.seasonMembershipId !== null) {
    throw new Error('Assignment selector is invalid');
  }
}

function assertOnlySeasonMembershipSelectorFields(selector: AssignmentDeliveryRuleTarget): void {
  if (selector.memberGroupId !== null || selector.seasonMembershipId === null) {
    throw new Error('Assignment selector is invalid');
  }
}

function assertOnlyReviewerMemberGroupFields(selector: AssignmentDeliveryRuleReviewer): void {
  if (selector.memberGroupId === null || selector.memberGroupTagId !== null || selector.seasonMembershipId !== null) {
    throw new Error('Assignment selector is invalid');
  }
}

function assertOnlyReviewerMemberGroupTagFields(selector: AssignmentDeliveryRuleReviewer): void {
  if (selector.memberGroupId !== null || selector.memberGroupTagId === null || selector.seasonMembershipId !== null) {
    throw new Error('Assignment selector is invalid');
  }
}

function assertOnlyReviewerSeasonMembershipFields(selector: AssignmentDeliveryRuleReviewer): void {
  if (selector.memberGroupId !== null || selector.memberGroupTagId !== null || selector.seasonMembershipId === null) {
    throw new Error('Assignment selector is invalid');
  }
}

function buildSelectorReferenceSets(input: {
  readonly members: readonly AssignmentRuleMember[];
  readonly knownMemberGroupIds: readonly string[];
  readonly knownMemberGroupTagIds: readonly string[];
}): SelectorReferenceSets {
  return {
    memberIds: new Set(input.members.map((member) => member.seasonMembershipId)),
    memberGroupIds: new Set(input.knownMemberGroupIds),
    memberGroupTagIds: new Set(input.knownMemberGroupTagIds)
  };
}

function orderedMemberIds(
  selectedIds: ReadonlySet<string>,
  members: readonly { readonly seasonMembershipId: string }[]
): readonly string[] {
  return members
    .filter((member) => selectedIds.has(member.seasonMembershipId))
    .map((member) => member.seasonMembershipId);
}

function asArray<T>(input: SelectorInput<T>): readonly T[] {
  return Array.isArray(input) ? (input as readonly T[]) : [input as T];
}

function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot === -1 ? '' : fileName.slice(lastDot + 1).toLowerCase();
}

function assertValidIsoDateString(value: string, errorMessage: string): number {
  return parseIsoDateTime(value, errorMessage);
}
